import React, { useState, useEffect } from 'react';
import { Card, Modal, Button, Form, Row, Col, Spinner, Badge, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faDownload, faTrash, faChevronLeft, faChevronRight, faSearchPlus, faSearchMinus, faFilter, faGlobe, faDesktop } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';

const ScreenshotGallery = ({ beacons }) => {
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [selectedBeacon, setSelectedBeacon] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [filterType, setFilterType] = useState(''); // '' = all, 'web' = web screenshots, 'desktop' = desktop screenshots

  // Client-side filter: web vs desktop screenshots
  const filteredScreenshots = filterType
    ? screenshots.filter(s => filterType === 'web' ? !!s.metadata?.url : !s.metadata?.url)
    : screenshots;

  const webCount = screenshots.filter(s => !!s.metadata?.url).length;
  const desktopCount = screenshots.length - webCount;

  useEffect(() => {
    loadScreenshots();
  }, [selectedBeacon, sortBy, sortOrder, page]);

  const loadScreenshots = async () => {
    setLoading(true);
    try {
      const filters = {
        type: 'screenshot',
        sort: sortBy,
        order: sortOrder,
        page: page,
        limit: 24
      };

      if (selectedBeacon) {
        filters.beaconId = selectedBeacon;
      }

      const response = await c2Service.getFiles(filters);
      const data = response.data?.data || [];
      setScreenshots(data);
      setTotalPages(response.data?.pages || 1);
    } catch (error) {
      console.error('Failed to load screenshots:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (screenshot, index) => {
    setSelectedScreenshot(screenshot);
    setCurrentIndex(index);
    setZoom(1);
    setShowLightbox(true);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    setSelectedScreenshot(null);
    setZoom(1);
  };

  const navigatePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : filteredScreenshots.length - 1;
    setCurrentIndex(newIndex);
    setSelectedScreenshot(filteredScreenshots[newIndex]);
    setZoom(1);
  };

  const navigateNext = () => {
    const newIndex = currentIndex < filteredScreenshots.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setSelectedScreenshot(filteredScreenshots[newIndex]);
    setZoom(1);
  };

  const handleDownload = async (screenshot) => {
    try {
      const response = await fetch(`/api/c2/files/${screenshot._id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = screenshot.metadata?.filename || `screenshot_${screenshot._id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download screenshot:', error);
      alert('Failed to download screenshot');
    }
  };

  const handleDelete = async (screenshot) => {
    if (!window.confirm('Delete this screenshot?')) return;

    try {
      await c2Service.deleteFile(screenshot._id);
      loadScreenshots();
      if (showLightbox) {
        closeLightbox();
      }
    } catch (error) {
      console.error('Failed to delete screenshot:', error);
      alert('Failed to delete screenshot');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedItems.size === 0) {
      alert('Please select screenshots to download');
      return;
    }

    for (const id of selectedItems) {
      const screenshot = screenshots.find(s => s._id === id);
      if (screenshot) {
        await handleDownload(screenshot);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setSelectedItems(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      alert('Please select screenshots to delete');
      return;
    }

    if (!window.confirm(`Delete ${selectedItems.size} screenshots?`)) return;

    try {
      for (const id of selectedItems) {
        await c2Service.deleteFile(id);
      }
      setSelectedItems(new Set());
      loadScreenshots();
    } catch (error) {
      console.error('Failed to delete screenshots:', error);
      alert('Failed to delete screenshots');
    }
  };

  const toggleSelection = (id) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedItems(newSelection);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <>
      <Card>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong style={{ color: '#fff' }}>Screenshots ({filteredScreenshots.length})</strong>
            {screenshots.length > 0 && (
              <div style={{ display: 'flex', gap: 0 }}>
                <button onClick={() => setFilterType('')}
                  style={{ padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #4b5563', borderRight: 'none', borderRadius: '4px 0 0 4px', cursor: 'pointer',
                    backgroundColor: filterType === '' ? '#4299e1' : 'transparent', color: filterType === '' ? '#fff' : '#9ca3af' }}>
                  All ({screenshots.length})
                </button>
                <button onClick={() => setFilterType('web')}
                  style={{ padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #4b5563', borderRight: 'none', cursor: 'pointer',
                    backgroundColor: filterType === 'web' ? '#4299e1' : 'transparent', color: filterType === 'web' ? '#fff' : '#9ca3af' }}>
                  <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '3px' }} />Web ({webCount})
                </button>
                <button onClick={() => setFilterType('desktop')}
                  style={{ padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #4b5563', borderRadius: '0 4px 4px 0', cursor: 'pointer',
                    backgroundColor: filterType === 'desktop' ? '#4299e1' : 'transparent', color: filterType === 'desktop' ? '#fff' : '#9ca3af' }}>
                  <FontAwesomeIcon icon={faDesktop} style={{ marginRight: '3px' }} />Desktop ({desktopCount})
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {selectedItems.size > 0 && (
              <>
                <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                  {selectedItems.size} selected
                </span>
                <button
                  onClick={handleBulkDownload}
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#3b82f6', color: '#fff', border: '1px solid #3b82f6', borderRadius: '0.375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download
                </button>
                <button
                  onClick={handleBulkDelete}
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#ef4444', color: '#fff', border: '1px solid #ef4444', borderRadius: '0.375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Delete
                </button>
              </>
            )}
            <Form.Select
              size="sm"
              value={selectedBeacon}
              onChange={(e) => {
                setSelectedBeacon(e.target.value);
                setPage(1);
              }}
              style={{ width: '200px' }}
            >
              <option value="">All Beacons</option>
              {beacons.map(beacon => (
                <option key={beacon._id} value={beacon.beaconId}>
                  {beacon.systemInfo?.hostname || beacon.beaconId}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              size="sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ width: '150px' }}
            >
              <option value="createdAt">Date</option>
              <option value="metadata.size">Size</option>
            </Form.Select>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#6b7280', color: '#fff', border: '1px solid #6b7280', borderRadius: '0.375rem', cursor: 'pointer' }}
            >
              {sortOrder === 'desc' ? '↓' : '↑'}
            </button>
            <button
              onClick={loadScreenshots}
              style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#6b7280', color: '#fff', border: '1px solid #6b7280', borderRadius: '0.375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <FontAwesomeIcon icon={faSync} />
              Refresh
            </button>
          </div>
        </div>

        <div style={{ padding: '1rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading screenshots...</p>
            </div>
          ) : screenshots.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem 0' }}>
              <p style={{ marginBottom: '0.5rem' }}>No screenshots found</p>
              <small>Capture screenshots via beacon tasks</small>
            </div>
          ) : (
            <>
              <Row>
                {filteredScreenshots.map((screenshot, index) => (
                  <Col key={screenshot._id} xs={12} sm={6} md={4} lg={3} className="mb-3">
                    <div
                      style={{
                        position: 'relative',
                        border: selectedItems.has(screenshot._id) ? '2px solid #3b82f6' : '1px solid #374151',
                        borderRadius: '0.5rem',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        backgroundColor: '#1f2937',
                        transition: 'transform 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedItems.has(screenshot._id)}
                        onChange={() => toggleSelection(screenshot._id)}
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          left: '0.5rem',
                          zIndex: 10,
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div onClick={() => openLightbox(screenshot, index)}>
                        <img
                          src={c2Service.getFileThumbnail(screenshot._id, 'medium')}
                          alt="Screenshot"
                          style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover'
                          }}
                          loading="lazy"
                        />
                        <div style={{ padding: '0.75rem' }}>
                          {/* Web screenshot: show URL + title */}
                          {screenshot.metadata?.url ? (
                            <>
                              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#e5e7eb', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                   title={screenshot.metadata.pageTitle || ''}>
                                <FontAwesomeIcon icon={faGlobe} style={{ color: '#4299e1', marginRight: '4px', fontSize: '10px' }} />
                                {screenshot.metadata.pageTitle || screenshot.metadata.host || 'Untitled'}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}
                                   title={screenshot.metadata.url}>
                                {screenshot.metadata.scheme}://{screenshot.metadata.host}:{screenshot.metadata.port}
                              </div>
                              {screenshot.metadata.statusCode && (
                                <Badge bg={screenshot.metadata.statusCode < 400 ? 'success' : screenshot.metadata.statusCode < 500 ? 'warning' : 'danger'}
                                       style={{ fontSize: '0.65rem', marginRight: '4px' }}>
                                  {screenshot.metadata.statusCode}
                                </Badge>
                              )}
                              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{formatDate(screenshot.createdAt)}</span>
                            </>
                          ) : (
                            <>
                              {/* Desktop screenshot: existing display */}
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e5e7eb', marginBottom: '0.25rem' }}>
                                <FontAwesomeIcon icon={faDesktop} style={{ color: '#a0aec0', marginRight: '4px', fontSize: '11px' }} />
                                {screenshot.beacon?.systemInfo?.hostname || 'Unknown'}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>
                                {formatDate(screenshot.createdAt)}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {formatBytes(screenshot.metadata?.size)}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    style={{
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem',
                      backgroundColor: page === 1 ? '#4b5563' : '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: page === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    style={{
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem',
                      backgroundColor: page === totalPages ? '#4b5563' : '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Lightbox Modal */}
      <Modal show={showLightbox} onHide={closeLightbox} size="xl" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderColor: '#374151' }}>
          <Modal.Title style={{ color: '#e5e7eb', fontSize: '1rem' }}>
            {selectedScreenshot?.metadata?.url ? (
              <>
                <FontAwesomeIcon icon={faGlobe} style={{ color: '#4299e1', marginRight: '8px' }} />
                {selectedScreenshot.metadata.pageTitle || selectedScreenshot.metadata.url}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDesktop} style={{ color: '#a0aec0', marginRight: '8px' }} />
                {selectedScreenshot?.beacon?.systemInfo?.hostname || 'Screenshot'}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#111827', padding: 0 }}>
          {selectedScreenshot && (
            <>
              <div style={{ position: 'relative', overflow: 'auto', maxHeight: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#000' }}>
                <img
                  src={`/api/c2/files/${selectedScreenshot._id}/preview`}
                  alt="Screenshot"
                  style={{
                    maxWidth: '100%',
                    transform: `scale(${zoom})`,
                    transition: 'transform 0.2s',
                    cursor: zoom > 1 ? 'zoom-out' : 'zoom-in'
                  }}
                  onClick={() => setZoom(zoom === 1 ? 2 : 1)}
                  onLoad={(e) => {
                    // Set image src to base64 from preview endpoint
                    fetch(`/api/c2/files/${selectedScreenshot._id}/preview`, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    })
                      .then(res => res.json())
                      .then(data => {
                        if (data.success && data.data?.content) {
                          e.target.src = `data:${data.data.mimeType};base64,${data.data.content}`;
                        }
                      })
                      .catch(err => console.error('Failed to load preview:', err));
                  }}
                />
                <button
                  onClick={navigatePrevious}
                  style={{
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '1.5rem'
                  }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  onClick={navigateNext}
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '1.5rem'
                  }}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#1f2937', borderTop: '1px solid #374151' }}>
                <Row>
                  <Col md={6}>
                    {selectedScreenshot.metadata?.url ? (
                      <>
                        <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                          <strong style={{ color: '#e5e7eb' }}>URL:</strong>{' '}
                          <span style={{ fontFamily: 'monospace' }}>{selectedScreenshot.metadata.url}</span>
                        </div>
                        <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                          <strong style={{ color: '#e5e7eb' }}>Title:</strong> {selectedScreenshot.metadata.pageTitle || '—'}
                        </div>
                        <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                          <strong style={{ color: '#e5e7eb' }}>Host:</strong> {selectedScreenshot.metadata.host}
                          {selectedScreenshot.metadata.hostname && ` (${selectedScreenshot.metadata.hostname})`}
                          {' • '}
                          <strong style={{ color: '#e5e7eb' }}>Port:</strong> {selectedScreenshot.metadata.port}
                          {' • '}
                          <strong style={{ color: '#e5e7eb' }}>Status:</strong>{' '}
                          <Badge bg={selectedScreenshot.metadata.statusCode < 400 ? 'success' : 'warning'}>
                            {selectedScreenshot.metadata.statusCode}
                          </Badge>
                        </div>
                      </>
                    ) : (
                      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                        <strong style={{ color: '#e5e7eb' }}>Beacon:</strong> {selectedScreenshot.beacon?.beaconId}
                      </div>
                    )}
                    <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      <strong style={{ color: '#e5e7eb' }}>Captured:</strong> {new Date(selectedScreenshot.createdAt).toLocaleString()}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      <strong style={{ color: '#e5e7eb' }}>Size:</strong> {formatBytes(selectedScreenshot.metadata?.size)}
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      <strong style={{ color: '#e5e7eb' }}>Beacon:</strong> {selectedScreenshot.beacon?.systemInfo?.hostname || selectedScreenshot.beacon?.beaconId || 'N/A'}
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      <strong style={{ color: '#e5e7eb' }}>Task:</strong> {selectedScreenshot.task?.command || 'N/A'}
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1f2937', borderColor: '#374151' }}>
          <ButtonGroup>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
            >
              <FontAwesomeIcon icon={faSearchMinus} /> Zoom Out
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setZoom(Math.min(3, zoom + 0.25))}
            >
              <FontAwesomeIcon icon={faSearchPlus} /> Zoom In
            </Button>
          </ButtonGroup>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleDownload(selectedScreenshot)}
          >
            <FontAwesomeIcon icon={faDownload} /> Download
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDelete(selectedScreenshot)}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete
          </Button>
          <Button variant="secondary" size="sm" onClick={closeLightbox}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ScreenshotGallery;
